import React from 'react';
import { Box, Container, Link, Stack, Typography } from '@mui/material';
import InstagramIcon from '@mui/icons-material/Instagram';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useStorefrontContext } from 'context/StorefrontContext';
import { ROUTES } from 'config/routes';
import { NavItem } from 'types/storefront';
import { resolveSiteName } from 'utils/themeDisplay';

const defaultFooter: NavItem[] = [
  { labelKey: 'nav.catalog', url: ROUTES.CATALOG },
  { labelKey: 'nav.promotions', url: ROUTES.PROMOTIONS },
];

const Footer: React.FC = () => {
  const { t } = useTranslation('common');
  const { navigation, theme } = useStorefrontContext();
  const items = navigation.footer.length > 0 ? navigation.footer : defaultFooter;
  const siteName = resolveSiteName(theme);
  const contact = theme?.contact;

  return (
    <Box component="footer" sx={{ bgcolor: 'grey.900', color: 'grey.100', mt: 'auto', py: 4 }}>
      <Container maxWidth="lg">
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} justifyContent="space-between">
          <Box>
            <Typography variant="h6">{siteName}</Typography>
            {contact && (
              <Stack spacing={0.5} mt={1.5}>
                {contact.phone && (
                  <Typography variant="body2" color="grey.300">
                    {contact.phone}
                  </Typography>
                )}
                {contact.email && (
                  <Link href={`mailto:${contact.email}`} color="inherit" underline="hover" variant="body2">
                    {contact.email}
                  </Link>
                )}
                {contact.address && (
                  <Typography variant="body2" color="grey.300">
                    {contact.address}
                  </Typography>
                )}
                {contact.instagramUrl && (
                  <Link
                    href={contact.instagramUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    color="inherit"
                    underline="hover"
                    variant="body2"
                    sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                  >
                    <InstagramIcon fontSize="small" />
                    Instagram
                  </Link>
                )}
              </Stack>
            )}
          </Box>
          <Stack direction="row" spacing={2} flexWrap="wrap">
            {items.map((item) => (
              <Link
                key={item.url}
                component={RouterLink}
                to={item.url}
                color="inherit"
                underline="hover"
              >
                {item.label || (item.labelKey ? t(item.labelKey) : item.url)}
              </Link>
            ))}
          </Stack>
        </Stack>
        <Typography variant="body2" color="grey.500" mt={3}>
          © {new Date().getFullYear()} {siteName}
        </Typography>
      </Container>
    </Box>
  );
};

export default Footer;
